import '@apps/web/routes/_app/admin/v3-event-components.css';

import { createFileRoute } from '@tanstack/react-router';
import { Activity, Bell, Clock } from 'lucide-react';
import { Fragment, type ReactNode, useEffect, useState } from 'react';

import {
    AdminBadge,
    type AdminBadgeTone,
} from '@apps/web/features/admin_design/admin_badge';
import {
    AdminCard,
    AdminPage,
    AdminPageBody,
    AdminPageHeader,
    AdminSection,
} from '@apps/web/features/admin_design/admin_shell';
import {
    AdminTable,
    AdminTableBody,
    AdminTableCell,
    AdminTableHeadCell,
    AdminTableHeader,
    AdminTableRow,
} from '@apps/web/features/admin_design/admin_table';
import {
    type KnownEventType,
    type RawCommittedEvent,
} from '@apps/web/features/phoebe_chat_v3/model/event_types';
import { type OutreachCardStatus } from '@apps/web/features/phoebe_chat_v3/model/outreach_card_status';
import { type QueuedUserMessage } from '@apps/web/features/phoebe_chat_v3/model/projection';
import { V3_PHOEBE_PRESETS } from '@apps/web/features/phoebe_chat_v3/profiles/v3_phoebe_profile';
import { composeDefinitionModules } from '@apps/web/features/phoebe_chat_v3/registry/compose_registry';
import { type SafeQuestionPresentation } from '@apps/web/features/phoebe_chat_v3/registry/definitions/tool_call';
import {
    collapsedApprovalTitle,
    ControlledPermissionView,
} from '@apps/web/features/phoebe_chat_v3/registry/definitions/tool_call_approval_views';
import { ControlledQuestionView } from '@apps/web/features/phoebe_chat_v3/registry/definitions/tool_call_question_views';
import { generatedDefinitionModules } from '@apps/web/features/phoebe_chat_v3/registry/generated_registry_input';
import {
    type ApprovalEditor,
    type ApprovalPresentation,
    type RegistryDefinitionShape,
    type RegistryOccurrence,
    type SafeRenderContext,
} from '@apps/web/features/phoebe_chat_v3/registry/types';
import { CHAT_COLUMN_CLASS } from '@apps/web/features/phoebe_chat_v3/ui/chat_column';
import {
    ChatHomePresetRows,
    ChatHomeStage,
} from '@apps/web/features/phoebe_chat_v3/ui/chat_home';
import {
    Composer,
    ComposerCard,
} from '@apps/web/features/phoebe_chat_v3/ui/composer';
import { AgencyRulesPreviewCard } from '@apps/web/features/phoebe_chat_v3/ui/primitives/agency_rules_preview_card';
import {
    type ApprovalStack,
    ApprovalStackContext,
    ApprovalStackSlot,
} from '@apps/web/features/phoebe_chat_v3/ui/primitives/approval_stack';
import {
    AttachmentCard,
    AttachmentImageCard,
    MarkdownPreviewBody,
} from '@apps/web/features/phoebe_chat_v3/ui/primitives/attachment_cards';
import {
    AssistantTextMessage,
    UserTextMessage,
} from '@apps/web/features/phoebe_chat_v3/ui/primitives/chat_text_message';
import { EventLine } from '@apps/web/features/phoebe_chat_v3/ui/primitives/event_line';
import { GeneratedTable } from '@apps/web/features/phoebe_chat_v3/ui/primitives/generated_table';
import { OutreachCard } from '@apps/web/features/phoebe_chat_v3/ui/primitives/outreach_card';
import { OutreachStatusCard } from '@apps/web/features/phoebe_chat_v3/ui/primitives/outreach_status_card';
import { PetalTickLoader } from '@apps/web/features/phoebe_chat_v3/ui/primitives/petal_tick_loader';
import { QuestionDockContext } from '@apps/web/features/phoebe_chat_v3/ui/question_dock';
import { QueuedMessages } from '@apps/web/features/phoebe_chat_v3/ui/queued_messages';
import { rawPersonName } from '@apps/web/lib/caregiver_name';
import { AssignFillPreview } from '@apps/web/routes/_app/admin/v3_assign_fill_preview';
import {
    AssistantPreviewRow,
    ChatPreviewFrame,
} from '@apps/web/routes/_app/admin/v3_event_component_preview_frame';
import { ReceiptTransitions } from '@apps/web/routes/_app/admin/v3_receipt_transitions';
import {
    SettingsWritePreview,
    ShiftWritePreview,
} from '@apps/web/routes/_app/admin/v3_write_card_previews';

const V3_EVENT_COMPONENT_REGISTRY = composeDefinitionModules(
    generatedDefinitionModules,
);

const SAMPLE_USER_ID = '00000000-0000-4000-8000-000000000001';
const SAMPLE_ORGANIZATION_ID = '00000000-0000-4000-8000-000000000002';
const SAMPLE_RUN_ID = '00000000-0000-4000-8000-000000000003';
const SAMPLE_CREATED_AT = '2026-09-01T14:30:00.000Z';
const SAMPLE_TIME_ZONE = 'America/New_York';

type EventDisposition = RegistryDefinitionShape['disposition'];

type SurfaceKind =
    | 'Message'
    | 'Chat shell'
    | 'Interactive event'
    | 'Activity event'
    | 'Lifecycle event'
    | 'Background event';

type EventComponentDetail = Readonly<{
    readonly surface: SurfaceKind;
    readonly component: string;
    readonly source: string;
    readonly role: string;
}>;

export type EventComponentRow = EventComponentDetail &
    Readonly<{
        readonly eventType: KnownEventType;
        readonly disposition: EventDisposition;
    }>;

type GalleryRenderBoundary =
    | 'Registry renderer'
    | 'Approval specialization'
    | 'Question specialization'
    | 'Harness chrome';

export type GalleryComponentItem = Readonly<{
    readonly id: string;
    readonly label: string;
    readonly component: string;
    readonly eventTypes: ReadonlyArray<string>;
    readonly renderBoundary: GalleryRenderBoundary;
    readonly source: string;
    readonly role: string;
}>;

const EVENT_COMPONENT_DETAILS: Partial<
    Record<KnownEventType, EventComponentDetail>
> = {
    user_message: {
        surface: 'Message',
        component: 'UserTextMessage',
        source: 'ui/primitives/chat_text_message.tsx',
        role: 'Right-aligned staff message bubble with sender context.',
    },
    assistant_text: {
        surface: 'Message',
        component: 'AssistantTextMessage',
        source: 'ui/primitives/chat_text_message.tsx',
        role: 'Phoebe response row with avatar, timestamp, markdown, and copy.',
    },
    tool_call: {
        surface: 'Interactive event',
        component: 'tool_call owner renderer',
        source: 'registry/definitions/tool_call.tsx',
        role: 'Owns safe tool activity and joins approval, question, result, and artifact slots.',
    },
    tool_approval_required: {
        surface: 'Interactive event',
        component: 'ControlledPermissionView',
        source: 'registry/definitions/tool_call_approval_views.tsx',
        role: 'Adds an approval card to the owning tool call.',
    },
    tool_approval_granted: {
        surface: 'Interactive event',
        component: 'ControlledPermissionView receipt',
        source: 'registry/definitions/tool_call_approval_views.tsx',
        role: 'Marks the approval interaction as allowed.',
    },
    tool_approval_denied: {
        surface: 'Interactive event',
        component: 'ControlledPermissionView receipt',
        source: 'registry/definitions/tool_call_approval_views.tsx',
        role: 'Marks the approval interaction as denied.',
    },
    user_question_required: {
        surface: 'Interactive event',
        component: 'ControlledQuestionView',
        source: 'registry/definitions/tool_call_question_views.tsx',
        role: 'Adds a user question card to the owning tool call.',
    },
    user_question_answered: {
        surface: 'Interactive event',
        component: 'ControlledQuestionView receipt',
        source: 'registry/definitions/tool_call_question_views.tsx',
        role: 'Records the selected answer against the owning tool call.',
    },
    user_question_cancelled: {
        surface: 'Interactive event',
        component: 'ControlledQuestionView receipt',
        source: 'registry/definitions/tool_call_question_views.tsx',
        role: 'Records that the requested answer was skipped.',
    },
    tool_output: {
        surface: 'Activity event',
        component: 'Tool result fragment',
        source: 'registry/definitions/tool_output.tsx',
        role: 'Attaches success or failure evidence to the owning tool call.',
    },
    artifact_updated: {
        surface: 'Activity event',
        component: 'Artifact fragment',
        source: 'registry/definitions/artifact_updated.tsx',
        role: 'Attaches generated artifact evidence to the owning tool call.',
    },
    assistant_reasoning: {
        surface: 'Activity event',
        component: 'Reasoning activity row',
        source: 'registry/definitions/assistant_reasoning.tsx',
        role: 'Shows safe reasoning-progress narration.',
    },
    status_update: {
        surface: 'Activity event',
        component: 'Status update row',
        source: 'registry/definitions/status_update.tsx',
        role: 'Shows safe progress updates as transcript activity.',
    },
    run_started: {
        surface: 'Lifecycle event',
        component: 'Lifecycle evidence',
        source: 'registry/definitions/run_started.tsx',
        role: 'Starts a run boundary without rendering customer copy.',
    },
    run_completed: {
        surface: 'Lifecycle event',
        component: 'Lifecycle evidence',
        source: 'registry/definitions/run_completed.tsx',
        role: 'Completes a run boundary without rendering customer copy.',
    },
    run_failed: {
        surface: 'Lifecycle event',
        component: 'Run failure row',
        source: 'registry/definitions/run_failed.tsx',
        role: 'Shows bounded failure copy for the run.',
    },
    run_interrupted: {
        surface: 'Lifecycle event',
        component: 'Run interrupted row',
        source: 'registry/definitions/run_interrupted.tsx',
        role: 'Shows that the current run stopped after an interrupt.',
    },
    poke: {
        surface: 'Activity event',
        component: 'Check-in event line',
        source: 'registry/definitions/poke.tsx',
        role: 'Shows a scheduled or fired check-in as one sentence that stays in the transcript.',
    },
    watch: {
        surface: 'Activity event',
        component: 'Watch event line',
        source: 'registry/definitions/watch.tsx',
        role: 'Shows each watch update as one sentence that stays in the transcript.',
    },
    domain_event_observed: {
        surface: 'Activity event',
        component: 'Domain event line',
        source: 'registry/definitions/domain_event_observed.tsx',
        role: 'Shows a subscribed domain event as one sentence that stays in the transcript.',
    },
} satisfies Partial<Record<KnownEventType, EventComponentDetail>>;

export const GALLERY_COMPONENTS = [
    {
        id: 'loader',
        label: 'Loader',
        component: 'PetalTickLoader',
        eventTypes: ['is_generating', 'pending_event'],
        renderBoundary: 'Harness chrome',
        source: 'ui/primitives/petal_tick_loader.tsx',
        role: 'Shows Phoebe working without creating a transcript event row.',
    },
    {
        id: 'chat-home',
        label: 'Chat home',
        component: 'ChatHarness empty state',
        eventTypes: ['run_state:empty', 'profile.starters'],
        renderBoundary: 'Harness chrome',
        source: 'ui/chat_harness.tsx',
        role: 'Initial chat surface before any durable transcript event exists.',
    },
    {
        id: 'approval-mode',
        label: 'Approval card',
        component: 'ControlledPermissionView',
        eventTypes: [
            'tool_call',
            'tool_approval_required',
            'tool_approval_granted',
            'tool_approval_denied',
        ],
        renderBoundary: 'Approval specialization',
        source: 'registry/definitions/tool_call_approval_views.tsx',
        role: 'Generic approval card for an action that needs a coordinator decision.',
    },
    {
        id: 'user-message',
        label: 'User message',
        component: 'UserTextMessage',
        eventTypes: ['user_message'],
        renderBoundary: 'Registry renderer',
        source: 'ui/primitives/chat_text_message.tsx',
        role: 'Committed staff-authored message row.',
    },
    {
        id: 'queued-messages',
        label: 'Queued messages',
        component: 'QueuedMessages',
        eventTypes: ['pending_event:user_message', 'optimistic_user_message'],
        renderBoundary: 'Harness chrome',
        source: 'ui/queued_messages.tsx',
        role: 'Submitted staff messages waiting to enter durable transcript history.',
    },
    {
        id: 'assistant-message',
        label: 'Assistant message',
        component: 'AssistantTextMessage',
        eventTypes: ['assistant_text'],
        renderBoundary: 'Registry renderer',
        source: 'ui/primitives/chat_text_message.tsx',
        role: 'Committed Phoebe response row.',
    },
    {
        id: 'agency-rules-preview',
        label: 'Agency rules preview',
        component: 'AgencyRulesPreviewCard',
        eventTypes: ['tool_call', 'tool_output'],
        renderBoundary: 'Registry renderer',
        source: 'registry/definitions/tool_call.tsx',
        role: 'Validated agency-rules artifact inside a Phoebe response.',
    },
    {
        id: 'generated-table',
        label: 'Generated table',
        component: 'GeneratedTableResource -> GeneratedTable',
        eventTypes: ['tool_call', 'tool_output.presentation:table'],
        renderBoundary: 'Registry renderer',
        source: 'ui/generated_table.tsx',
        role: 'Validated CSV or JSONL artifact table with paging, row selection, and follow-up actions.',
    },
    {
        id: 'attachment-chip',
        label: 'Attachment card',
        component: 'V3ChatAttachmentCard -> AttachmentCard',
        eventTypes: ['tool_call', 'tool_output.presentation:chat-attachment'],
        renderBoundary: 'Registry renderer',
        source: 'ui/primitives/attachment_cards.tsx',
        role: 'Delivered file typed by its filename extension. The card opens the file; the icon downloads it.',
    },
    {
        id: 'attachment-image',
        label: 'Attachment image',
        component: 'V3ChatAttachmentCard -> AttachmentImageCard',
        eventTypes: ['tool_call', 'tool_output.presentation:chat-attachment'],
        renderBoundary: 'Registry renderer',
        source: 'ui/primitives/attachment_cards.tsx',
        role: 'Delivered image rendered inline. The image opens the file; the overlay icon downloads it.',
    },
    {
        id: 'attachment-markdown',
        label: 'Attachment report',
        component:
            'V3ChatAttachmentCard -> AttachmentCard + MarkdownPreviewBody',
        eventTypes: ['tool_call', 'tool_output.presentation:chat-attachment'],
        renderBoundary: 'Registry renderer',
        source: 'ui/primitives/attachment_cards.tsx',
        role: 'Delivered markdown report as a clamped preview. The card opens the full report.',
    },
    {
        id: 'ask-user',
        label: 'Ask User',
        component: 'ControlledQuestionView',
        eventTypes: [
            'tool_call',
            'user_question_required',
            'user_question_answered',
            'user_question_cancelled',
        ],
        renderBoundary: 'Question specialization',
        source: 'registry/definitions/tool_call_question_views.tsx',
        role: 'Question card joined into the owning tool call row.',
    },
    {
        id: 'docked-question',
        label: 'Docked question',
        component: 'ControlledQuestionView -> QuestionComposerPanel',
        eventTypes: ['tool_call', 'user_question_required'],
        renderBoundary: 'Question specialization',
        source: 'ui/primitives/question_composer_panel.tsx',
        role: 'Pending question wrapping the live composer without blocking chat.',
    },
    {
        id: 'answered-question',
        label: 'Answered question',
        component: 'ControlledQuestionView receipt',
        eventTypes: ['tool_call', 'user_question_answered'],
        renderBoundary: 'Question specialization',
        source: 'registry/definitions/tool_call_question_views.tsx',
        role: 'Resolved question receipt after the coordinator answers.',
    },
    {
        id: 'message',
        label: 'Message approval',
        component: 'ControlledPermissionView',
        eventTypes: [
            'tool_call',
            'tool_approval_required',
            'tool_approval_granted',
            'tool_approval_denied',
        ],
        renderBoundary: 'Approval specialization',
        source: 'registry/definitions/tool_call_approval_views.tsx',
        role: 'SMS review card with recipients, editable message, and preview.',
    },
    {
        id: 'voice',
        label: 'Call approval',
        component: 'ControlledPermissionView',
        eventTypes: [
            'tool_call',
            'tool_approval_required',
            'tool_approval_granted',
            'tool_approval_denied',
        ],
        renderBoundary: 'Approval specialization',
        source: 'registry/definitions/tool_call_approval_views.tsx',
        role: 'Voice call review card with one swappable recipient and purpose.',
    },
    {
        id: 'outreach-approval',
        label: 'Outreach approval',
        component: 'ControlledPermissionView -> OutreachCard',
        eventTypes: [
            'tool_call',
            'tool_approval_required',
            'tool_approval_granted',
            'tool_approval_denied',
        ],
        renderBoundary: 'Approval specialization',
        source: 'registry/definitions/tool_call_approval_views.tsx',
        role: 'Review and approve an outreach before it is created.',
    },
    {
        id: 'outreach',
        label: 'Outreach',
        component: 'LiveOutreachCard -> OutreachStatusCard',
        eventTypes: ['tool_call', 'tool_output'],
        renderBoundary: 'Registry renderer',
        source: 'ui/primitives/outreach_status_card.tsx',
        role: 'Outreach lifecycle status with a quiet Running pulse and a View outreach action.',
    },
    {
        id: 'check-in-events',
        label: 'Check-in events',
        component: 'EventLine',
        eventTypes: ['poke', 'watch'],
        renderBoundary: 'Registry renderer',
        source: 'ui/primitives/event_line.tsx',
        role: 'Canonical one-line activity receipts for check-ins and watches.',
    },
    {
        id: 'assign-fill',
        label: 'Assign & fill',
        component: 'ControlledPermissionView -> AssignFillCard',
        eventTypes: ['tool_call', 'tool_approval_required'],
        renderBoundary: 'Approval specialization',
        source: 'ui/primitives/assign_fill_card.tsx',
        role: 'One card per caregiver using the chat approval flow: select shifts, assign, wait for confirmation, then celebrate in purple.',
    },
    {
        id: 'shift-write',
        label: 'Shift changes',
        component: 'CompactWriteCard',
        eventTypes: ['tool_approval_required', 'tool_output'],
        renderBoundary: 'Approval specialization',
        source: 'ui/primitives/compact_write_card.tsx',
        role: 'Compact shift approvals and confirmed receipts, with change details and material notices.',
    },
    {
        id: 'settings-write',
        label: 'Agency settings',
        component: 'SettingsApprovalPanel -> SettingsReceiptCard',
        eventTypes: ['tool_approval_required', 'tool_output'],
        renderBoundary: 'Approval specialization',
        source: 'ui/primitives/settings_approval_panel.tsx',
        role: 'Editable settings followed by actual saved toggles and values.',
    },
    {
        id: 'generic-approval',
        label: 'Generic approval',
        component: 'ControlledPermissionView',
        eventTypes: [
            'tool_call',
            'tool_approval_required',
            'tool_approval_granted',
            'tool_approval_denied',
        ],
        renderBoundary: 'Approval specialization',
        source: 'registry/definitions/tool_call_approval_views.tsx',
        role: 'Fallback permission panel for gated tools without a custom card.',
    },
    {
        id: 'approval-stack',
        label: 'Approval stack',
        component: 'ApprovalStackSlot',
        eventTypes: ['tool_approval_required (simultaneous)'],
        renderBoundary: 'Approval specialization',
        source: 'ui/primitives/approval_stack.tsx',
        role: 'Accordion for simultaneous pending approvals: one expanded card, the rest one-line waiting rows.',
    },
] as const satisfies ReadonlyArray<GalleryComponentItem>;

type GalleryComponentId = (typeof GALLERY_COMPONENTS)[number]['id'];

const DISPOSITION_COPY = {
    render: 'Rendered',
    compound_owner: 'Owner',
    compound_member: 'Member',
    invisible: 'Invisible',
} satisfies Record<EventDisposition, string>;

const DISPOSITION_TONE = {
    render: 'ok',
    compound_owner: 'info',
    compound_member: 'warn',
    invisible: 'neutral',
} satisfies Record<EventDisposition, AdminBadgeTone>;

export const Route = createFileRoute('/_app/admin/v3-event-components')({
    head: () => ({
        meta: [
            {
                title: 'V3 Event Components | Phoebe Admin',
            },
        ],
    }),
    component: V3EventComponentsPage,
});

function fallbackDetail(
    eventType: KnownEventType,
    disposition: EventDisposition,
): EventComponentDetail {
    switch (disposition) {
        case 'render':
            return {
                surface: 'Activity event',
                component: `${eventType} renderer`,
                source: `registry/definitions/${eventType}.tsx`,
                role: 'Renders a standalone transcript row from safe payload fields.',
            };
        case 'compound_owner':
            return {
                surface: 'Interactive event',
                component: `${eventType} owner renderer`,
                source: `registry/definitions/${eventType}.tsx`,
                role: 'Owns a transcript row that later events can enrich.',
            };
        case 'compound_member':
            return {
                surface: 'Interactive event',
                component: `${eventType} slot member`,
                source: `registry/definitions/${eventType}.tsx`,
                role: 'Updates another event component instead of rendering alone.',
            };
        case 'invisible':
            return {
                surface: 'Background event',
                component: 'No visible component',
                source: `registry/definitions/${eventType}.tsx`,
                role: 'Feeds lifecycle, safety, or telemetry state without customer-visible UI.',
            };
        default: {
            const exhaustiveDisposition: never = disposition;
            return exhaustiveDisposition;
        }
    }
}

export function getV3EventComponentRows(): ReadonlyArray<EventComponentRow> {
    return (Object.keys(V3_EVENT_COMPONENT_REGISTRY) as Array<KnownEventType>)
        .sort((left, right) => left.localeCompare(right))
        .map((eventType) => {
            const definition = V3_EVENT_COMPONENT_REGISTRY[eventType];
            const detail =
                EVENT_COMPONENT_DETAILS[eventType] ??
                fallbackDetail(eventType, definition.disposition);
            return {
                eventType,
                disposition: definition.disposition,
                ...detail,
            };
        });
}

function makeOccurrence<K extends 'assistant_text' | 'user_message'>(
    type: K,
    text: string,
    sentBy: RawCommittedEvent['sent_by'],
): RegistryOccurrence<K> {
    const id = `00000000-0000-4000-8000-${type === 'user_message' ? '000000000101' : '000000000102'}`;
    return {
        occurrenceId: `committed-event:${id}`,
        type,
        phase: 'committed',
        committed: {
            id,
            event_offset: type === 'user_message' ? 1 : 2,
            type,
            agent_item_id: null,
            command_id: null,
            caused_by_event_id: null,
            source_pending_event_id: null,
            external_ref: null,
            payload: { text },
            created_at: SAMPLE_CREATED_AT,
            slack_user_mentions: null,
            sent_by: sentBy,
        },
        pending: null,
    };
}

const SAMPLE_USER_MESSAGE_OCCURRENCE = makeOccurrence(
    'user_message',
    'Can you check the open callouts for tomorrow?',
    {
        user_id: SAMPLE_USER_ID,
        display_name: 'Agent User',
    },
);

const SAMPLE_ASSISTANT_MESSAGE_OCCURRENCE = makeOccurrence(
    'assistant_text',
    'I found two open callouts. The overnight case needs approval before I contact caregivers.',
    null,
);

const SAMPLE_COMPOSER_PLACEHOLDER = 'Message Phoebe…';

const SAMPLE_QUEUED_MESSAGES = [
    {
        occurrenceId: 'pending-event:sample-queued-message',
        text: 'Draft the outreach, but ask before sending.',
    },
    {
        occurrenceId: 'local-operation:sample-follow-up-message',
        text: 'Prioritize caregivers who have worked this case before.',
    },
] satisfies ReadonlyArray<QueuedUserMessage>;

const SAMPLE_GENERATED_TABLE_ROWS = [
    {
        shift_id: '00000000-0000-4000-8000-000000000301',
        client: 'Mrs. Benton',
        start_time: '2026-09-02T22:00:00',
        status: 'open_shift',
        requested_hours: 8,
        caregiver: null,
    },
    {
        shift_id: '00000000-0000-4000-8000-000000000302',
        client: 'Mr. Alvarez',
        start_time: '2026-09-03T09:00:00',
        status: 'awaiting_confirmation',
        requested_hours: 4,
        caregiver: { id: 'cg-1', name: 'Rosa M.' },
    },
    {
        shift_id: '00000000-0000-4000-8000-000000000303',
        client: 'Harbor House',
        start_time: '2026-09-03T13:00:00',
        status: 'open_shift',
        requested_hours: 5,
        caregiver: null,
    },
] satisfies ReadonlyArray<Readonly<Record<string, unknown>>>;

const SAMPLE_APPROVAL_PRESENTATION = {
    question: 'Can I send these texts?',
    groups: [
        {
            heading: 'Recipients',
            items: ['Mia Chen', 'Jordan Lee'],
        },
        {
            heading: 'Message',
            items: [
                'Hi {{first_name}}, are you available for the Benton overnight shift tomorrow?',
            ],
        },
    ],
} satisfies ApprovalPresentation;

const SAMPLE_APPROVAL_EDITOR = {
    kind: 'sms',
    recipients: [
        {
            handle_type: 'caregiver_id',
            handle_id: '00000000-0000-4000-8000-000000000201',
            name: 'Mia Chen',
            message:
                'Hi {{first_name}}, are you available for the Benton overnight shift tomorrow?',
        },
        {
            handle_type: 'caregiver_id',
            handle_id: '00000000-0000-4000-8000-000000000202',
            name: 'Jordan Lee',
            message:
                'Hi {{first_name}}, are you available for the Benton overnight shift tomorrow?',
        },
    ],
} satisfies ApprovalEditor;

const SAMPLE_VOICE_APPROVAL_PRESENTATION = {
    question: 'Can I call Mia Chen?',
    groups: [
        { heading: 'Recipient', items: ['Mia Chen'] },
        {
            heading: 'Purpose',
            items: [
                'Ask whether she can cover the Benton overnight shift tomorrow, and offer the Tuesday morning visit if she declines.',
            ],
        },
        {
            heading: 'Opening line',
            items: ['Hi Mia, this is Phoebe from Benton Home Care.'],
        },
    ],
} satisfies ApprovalPresentation;

const SAMPLE_VOICE_APPROVAL_EDITOR = {
    kind: 'voice',
    handle_type: 'caregiver_id',
    handle_id: '00000000-0000-4000-8000-000000000201',
    name: 'Mia Chen',
    instructions:
        'Ask whether she can cover the Benton overnight shift tomorrow, and offer the Tuesday morning visit if she declines.',
    opening_line: 'Hi Mia, this is Phoebe from Benton Home Care.',
} satisfies ApprovalEditor;

const SAMPLE_QUESTION_PRESENTATION = {
    question: 'Which caregivers should Phoebe try first for Mia Chen?',
    header: null,
    input_type: 'single_choice',
    choices: [
        {
            label: 'Caregivers who have visited this client before',
            description: 'Anyone new waits until they have said no.',
        },
        {
            label: 'Caregivers with the best availability',
            description: null,
        },
    ],
    placeholder: null,
    allow_other: false,
    presentation: {
        type: 'actions',
        title: null,
        description: null,
        actions: [
            { value: null, label: null, style: 'primary' },
            { value: null, label: null, style: 'secondary' },
        ],
    },
} satisfies SafeQuestionPresentation;

const SAMPLE_MULTI_CHOICE_QUESTION_PRESENTATION = {
    question: 'Which caregivers should Phoebe contact first?',
    header: 'Caregivers',
    input_type: 'multi_choice',
    choices: [
        {
            label: 'Prior visits',
            description: null,
        },
        {
            label: 'Best availability',
            description: null,
        },
        {
            label: 'Nearby',
            description: null,
        },
    ],
    placeholder: 'Add another group',
    allow_other: true,
    presentation: null,
} satisfies SafeQuestionPresentation;

const SAMPLE_GENERIC_APPROVAL_PRESENTATION = {
    question: 'Create the scheduled review "Morning uncovered shifts"?',
    groups: [
        {
            heading: 'Schedule',
            items: ['Weekdays at 7:00 AM', 'Checks uncovered shifts'],
        },
        {
            heading: 'Result',
            items: ['Messages you a summary each run'],
        },
    ],
} satisfies ApprovalPresentation;

const NOOP_RENDER_CONTEXT: SafeRenderContext = {
    scope: {
        userId: SAMPLE_USER_ID,
        organizationId: SAMPLE_ORGANIZATION_ID,
        mode: 'sandbox',
        runId: SAMPLE_RUN_ID,
        organizationTimeZone: SAMPLE_TIME_ZONE,
    },
    nowMs: Date.parse(SAMPLE_CREATED_AT),
    organizationTimeZone: SAMPLE_TIME_ZONE,
    formatPersonName: rawPersonName,
    isExpanded: false,
    requestGroupToggle: () => undefined,
    requestRetry: () => undefined,
    requestQuestionSubmit: () => Promise.resolve('accepted'),
    requestToolDecision: () => Promise.resolve('accepted'),
    requestSendMessage: () => undefined,
    requestNavigate: () => undefined,
};

function surfaceLabel(surface: SurfaceKind): ReactNode {
    const tone =
        surface === 'Message'
            ? 'ok'
            : surface === 'Interactive event'
              ? 'info'
              : surface === 'Lifecycle event'
                ? 'warn'
                : 'neutral';
    return <AdminBadge tone={tone}>{surface}</AdminBadge>;
}

function V3EventComponentsPage(): ReactNode {
    const rows = getV3EventComponentRows();

    return (
        <AdminPage className="v3-event-components-page">
            <AdminPageHeader
                title="V3 Event Components"
                meta="phoebe_chat_v3"
            />
            <AdminPageBody>
                <div className="flex max-w-7xl flex-col gap-8">
                    <AdminSection
                        title="Card transitions"
                        description="Follow a request from its active card to a compact receipt in chat."
                    >
                        <ReceiptTransitions />
                    </AdminSection>
                    <AdminSection
                        title="Component Registry"
                        description="Simple rendered examples of the chat components and states the registry needs to cover."
                    >
                        <ComponentRegistry />
                    </AdminSection>
                    <AdminSection
                        title="Dynamic Render Table"
                        description="The gallery list mapped to the V3 event or state boundary that renders it."
                    >
                        <DynamicRenderTable />
                    </AdminSection>
                    <AdminSection
                        title="Registry Map"
                        description="Every generated V3 event type and the component boundary it enters."
                    >
                        <AdminCard flush={true}>
                            <AdminTable density="compact">
                                <AdminTableHeader>
                                    <AdminTableRow>
                                        <AdminTableHeadCell>
                                            Event type
                                        </AdminTableHeadCell>
                                        <AdminTableHeadCell>
                                            Surface
                                        </AdminTableHeadCell>
                                        <AdminTableHeadCell>
                                            Disposition
                                        </AdminTableHeadCell>
                                        <AdminTableHeadCell>
                                            Component
                                        </AdminTableHeadCell>
                                        <AdminTableHeadCell>
                                            Source
                                        </AdminTableHeadCell>
                                        <AdminTableHeadCell>
                                            Role
                                        </AdminTableHeadCell>
                                    </AdminTableRow>
                                </AdminTableHeader>
                                <AdminTableBody>
                                    {rows.map((row) => (
                                        <AdminTableRow key={row.eventType}>
                                            <AdminTableCell>
                                                <code className="text-admin-xs text-admin-ink admin-tabular font-mono">
                                                    {row.eventType}
                                                </code>
                                            </AdminTableCell>
                                            <AdminTableCell>
                                                {surfaceLabel(row.surface)}
                                            </AdminTableCell>
                                            <AdminTableCell>
                                                <AdminBadge
                                                    tone={
                                                        DISPOSITION_TONE[
                                                            row.disposition
                                                        ]
                                                    }
                                                >
                                                    {
                                                        DISPOSITION_COPY[
                                                            row.disposition
                                                        ]
                                                    }
                                                </AdminBadge>
                                            </AdminTableCell>
                                            <AdminTableCell className="text-admin-ink font-medium">
                                                {row.component}
                                            </AdminTableCell>
                                            <AdminTableCell>
                                                <code className="text-admin-xs admin-tabular font-mono">
                                                    {row.source}
                                                </code>
                                            </AdminTableCell>
                                            <AdminTableCell className="min-w-72">
                                                {row.role}
                                            </AdminTableCell>
                                        </AdminTableRow>
                                    ))}
                                </AdminTableBody>
                            </AdminTable>
                        </AdminCard>
                    </AdminSection>
                </div>
            </AdminPageBody>
        </AdminPage>
    );
}

function DynamicRenderTable(): ReactNode {
    return (
        <AdminCard flush={true}>
            <AdminTable density="compact">
                <AdminTableHeader>
                    <AdminTableRow>
                        <AdminTableHeadCell>
                            Gallery component
                        </AdminTableHeadCell>
                        <AdminTableHeadCell>Render boundary</AdminTableHeadCell>
                        <AdminTableHeadCell>Event / state</AdminTableHeadCell>
                        <AdminTableHeadCell>Component</AdminTableHeadCell>
                        <AdminTableHeadCell>Source</AdminTableHeadCell>
                        <AdminTableHeadCell>Role</AdminTableHeadCell>
                    </AdminTableRow>
                </AdminTableHeader>
                <AdminTableBody>
                    {GALLERY_COMPONENTS.map((item) => (
                        <AdminTableRow key={item.id}>
                            <AdminTableCell className="text-admin-ink font-medium">
                                {item.label}
                            </AdminTableCell>
                            <AdminTableCell>
                                <AdminBadge tone="info">
                                    {item.renderBoundary}
                                </AdminBadge>
                            </AdminTableCell>
                            <AdminTableCell>
                                <div className="flex min-w-56 flex-wrap gap-1">
                                    {item.eventTypes.map((eventType) => (
                                        <AdminBadge
                                            key={eventType}
                                            tone="neutral"
                                        >
                                            {eventType}
                                        </AdminBadge>
                                    ))}
                                </div>
                            </AdminTableCell>
                            <AdminTableCell className="text-admin-ink font-medium">
                                {item.component}
                            </AdminTableCell>
                            <AdminTableCell>
                                <code className="text-admin-xs admin-tabular font-mono">
                                    {item.source}
                                </code>
                            </AdminTableCell>
                            <AdminTableCell className="min-w-72">
                                {item.role}
                            </AdminTableCell>
                        </AdminTableRow>
                    ))}
                </AdminTableBody>
            </AdminTable>
        </AdminCard>
    );
}

function ComponentRegistry(): ReactNode {
    return (
        <div data-testid="v3-component-registry" className="v3-registry-grid">
            {GALLERY_COMPONENTS.map((item) => (
                <RegistryExample key={item.id} item={item}>
                    {chatPlacementPreview(item.id)}
                </RegistryExample>
            ))}
        </div>
    );
}

function chatPlacementPreview(itemId: GalleryComponentId): ReactNode {
    const preview = galleryPreview(itemId);

    switch (itemId) {
        case 'chat-home':
            return (
                <ChatPreviewFrame variant="empty">
                    <ChatHomePreview />
                </ChatPreviewFrame>
            );
        case 'approval-mode':
            return (
                <ChatPreviewFrame>
                    <AssistantPreviewRow intro={assistantIntroFor(itemId)} />
                    {preview}
                </ChatPreviewFrame>
            );
        case 'user-message':
        case 'assistant-message':
            return <ChatPreviewFrame>{preview}</ChatPreviewFrame>;
        case 'queued-messages':
            return (
                <ChatPreviewFrame
                    footer={
                        <Fragment>
                            <div className={CHAT_COLUMN_CLASS}>{preview}</div>
                            <PreviewComposer />
                        </Fragment>
                    }
                >
                    <AssistantTextMessage
                        text="I’m still checking the open callouts. I’ll work through your follow-ups next."
                        occurrence={SAMPLE_ASSISTANT_MESSAGE_OCCURRENCE}
                        organizationTimeZone={SAMPLE_TIME_ZONE}
                    />
                </ChatPreviewFrame>
            );
        case 'loader':
            return preview;
        case 'ask-user':
            return <ChatPreviewFrame>{preview}</ChatPreviewFrame>;
        case 'docked-question':
            // The docked panel lives in the composer footer, not the feed.
            return (
                <ChatPreviewFrame footer={preview}>
                    <AssistantPreviewRow intro={assistantIntroFor(itemId)} />
                </ChatPreviewFrame>
            );
        case 'answered-question':
        case 'agency-rules-preview':
        case 'generated-table':
        case 'attachment-chip':
        case 'attachment-image':
        case 'attachment-markdown':
        case 'outreach-approval':
        case 'outreach':
        case 'assign-fill':
            return (
                <ChatPreviewFrame>
                    <AssistantPreviewRow intro={assistantIntroFor(itemId)} />
                    {preview}
                </ChatPreviewFrame>
            );
        case 'message':
        case 'voice':
        case 'shift-write':
        case 'settings-write':
        case 'generic-approval':
        case 'approval-stack':
            return <ChatPreviewFrame>{preview}</ChatPreviewFrame>;
        case 'check-in-events':
            return (
                <ChatPreviewFrame>
                    <div className="v3-preview-event-row">{preview}</div>
                </ChatPreviewFrame>
            );
        default: {
            const exhaustiveItemId: never = itemId;
            return exhaustiveItemId;
        }
    }
}

// The queued stack's whole contract is where it sits, so its placement
// preview renders the real composer beneath it instead of an empty band.
function PreviewComposer(): ReactNode {
    return (
        <ComposerCard
            canSubmit={false}
            displayName="Phoebe"
            feature={{
                kind: 'text',
                placeholder: SAMPLE_COMPOSER_PLACEHOLDER,
                send: () =>
                    Promise.reject(new Error('Gallery preview cannot send.')),
            }}
            onDeliver={() => undefined}
            onValueChange={() => undefined}
            placeholder={SAMPLE_COMPOSER_PLACEHOLDER}
            value=""
        />
    );
}

function galleryPreview(itemId: GalleryComponentId): ReactNode {
    switch (itemId) {
        case 'loader':
            return (
                <div className="v3-loader-inline">
                    <PetalTickLoader
                        className="v3-loader-inline-mark text-muted-foreground"
                        label="Phoebe is thinking"
                    />
                    <span>Phoebe is thinking…</span>
                </div>
            );
        case 'chat-home':
            return <ChatHomePreview />;
        case 'approval-mode':
            return <AnimatedGenericApprovalPreview />;
        case 'user-message':
            return (
                <div className="v3-registry-message-preview">
                    <UserTextMessage
                        text="Can you check the open callouts for tomorrow?"
                        occurrence={SAMPLE_USER_MESSAGE_OCCURRENCE}
                        organizationTimeZone={SAMPLE_TIME_ZONE}
                    />
                </div>
            );
        case 'queued-messages':
            return <QueuedMessages messages={SAMPLE_QUEUED_MESSAGES} />;
        case 'assistant-message':
            return (
                <div className="v3-registry-message-preview">
                    <AssistantTextMessage
                        text="I found two open callouts. The overnight case needs approval before I contact caregivers."
                        occurrence={SAMPLE_ASSISTANT_MESSAGE_OCCURRENCE}
                        organizationTimeZone={SAMPLE_TIME_ZONE}
                    />
                </div>
            );
        case 'agency-rules-preview':
            return (
                <AgencyRulesPreviewCard
                    agencyName="Harbor Home Care"
                    rules={[
                        'Confirm open shifts by phone, then SMS',
                        'Escalate callouts within 2 hours of start',
                    ]}
                    totalRuleCount={4}
                    onViewAll={() => undefined}
                />
            );
        case 'generated-table':
            return <GeneratedTablePreview />;
        case 'attachment-chip':
            return (
                <AttachmentCard
                    filename="weekly_fill_rates.csv"
                    sizeBytes={41 * 1024}
                    shapeLine="312+ rows × 6 columns"
                    isDownloading={false}
                    downloadError={null}
                    onOpen={() => undefined}
                    onDownload={() => undefined}
                />
            );
        case 'attachment-image':
            return (
                <AttachmentImageCard
                    filename="fill_rates_week_36.png"
                    sizeBytes={84 * 1024}
                    imageUrl={SAMPLE_ATTACHMENT_CHART_URL}
                    isDownloading={false}
                    downloadError={null}
                    onOpen={() => undefined}
                    onDownload={() => undefined}
                />
            );
        case 'attachment-markdown':
            return <AttachmentMarkdownPreview />;
        case 'ask-user':
            return <AskUserFlowPreview />;
        case 'docked-question':
            return <DockedQuestionPreview />;
        case 'answered-question':
            return (
                <div className="v3-registry-interaction-preview">
                    <ControlledQuestionView
                        callId="sample-answered-question-call"
                        choiceValues={null}
                        presentation={SAMPLE_MULTI_CHOICE_QUESTION_PRESENTATION}
                        requestSubmit={
                            NOOP_RENDER_CONTEXT.requestQuestionSubmit
                        }
                        resolvedAnswer={['Prior visits', 'Nearby']}
                        status="answered"
                    />
                </div>
            );
        case 'message':
            return <AnimatedMessageApprovalPreview />;
        case 'voice':
            return <AnimatedVoiceApprovalPreview />;
        case 'outreach-approval':
            return <OutreachApprovalPreview />;
        case 'outreach':
            return <AnimatedOutreachPreview />;
        case 'check-in-events':
            return <CheckInEventsPreview />;
        case 'assign-fill':
            return <AssignFillPreview scope={NOOP_RENDER_CONTEXT.scope} />;
        case 'shift-write':
            return <ShiftWritePreview />;
        case 'settings-write':
            return <SettingsWritePreview />;
        case 'generic-approval':
            return <AnimatedGenericApprovalPreview />;
        case 'approval-stack':
            return <ApprovalStackPreview />;
        default: {
            const exhaustiveItemId: never = itemId;
            return exhaustiveItemId;
        }
    }
}

function assistantIntroFor(itemId: GalleryComponentId): string | null {
    switch (itemId) {
        case 'ask-user':
        case 'docked-question':
            return 'I need your direction before I continue.';
        case 'answered-question':
            return 'I’ll use your answer and continue from here.';
        case 'agency-rules-preview':
            return 'Here’s a preview of Harbor Home Care’s rules.';
        case 'attachment-chip':
            return 'Here’s the export you asked for.';
        case 'attachment-image':
            return 'Here’s last week’s fill-rate chart.';
        case 'attachment-markdown':
            return 'Here’s the write-up of what I found.';
        case 'message':
            return 'Review this message before I send anything.';
        case 'voice':
            return 'Review this call before I dial anyone.';
        case 'outreach':
            return 'I created the outreach. You can follow its progress here.';
        case 'assign-fill':
            return 'Review the proposed assignment before I update the schedule.';
        case 'approval-mode':
        case 'generic-approval':
            return 'This action needs approval because it can run on its own.';
        default:
            return null;
    }
}

function ChatHomePreview(): ReactNode {
    return (
        <div className="v3-chat-home-preview">
            <ChatHomeStage greeting="How can I help today?" />
            <ChatHomePresetRows
                presets={V3_PHOEBE_PRESETS}
                onSelect={() => undefined}
            />
        </div>
    );
}

// Static stand-in for a delivered chart image so the gallery renders the
// loaded state without any network fetch.
const SAMPLE_ATTACHMENT_CHART_URL = `data:image/svg+xml;utf8,${encodeURIComponent(
    '<svg xmlns="http://www.w3.org/2000/svg" width="440" height="200">' +
        '<rect width="440" height="200" fill="white"/>' +
        '<g fill="#5533EE"><rect x="30" y="80" width="40" height="90" rx="4"/>' +
        '<rect x="85" y="60" width="40" height="110" rx="4"/>' +
        '<rect x="140" y="90" width="40" height="80" rx="4"/>' +
        '<rect x="195" y="45" width="40" height="125" rx="4"/>' +
        '<rect x="250" y="70" width="40" height="100" rx="4"/></g>' +
        '<g fill="#C9B8FA"><rect x="305" y="120" width="40" height="50" rx="4"/>' +
        '<rect x="360" y="112" width="40" height="58" rx="4"/></g></svg>',
)}`;

const SAMPLE_ATTACHMENT_REPORT_MARKDOWN = [
    '# Week 36 fill-rate report',
    '',
    'Fill rates averaged 74% across the week, up 3 points from week 35.',
    'Weekday performance was strong — Thursday peaked at 90% after the new',
    'outreach batch went out Wednesday evening.',
    '',
    'Weekend coverage remains the main gap: Saturday filled 40% and Sunday',
    '46%, both driven by three recurring overnight shifts.',
].join('\n');

function AttachmentMarkdownPreview(): ReactNode {
    return (
        <AttachmentCard
            filename="week_36_report.md"
            sizeBytes={6 * 1024}
            isDownloading={false}
            downloadError={null}
            onOpen={() => undefined}
            onDownload={() => undefined}
        >
            <MarkdownPreviewBody markdown={SAMPLE_ATTACHMENT_REPORT_MARKDOWN} />
        </AttachmentCard>
    );
}

function GeneratedTablePreview(): ReactNode {
    return (
        <div className="v3-generated-table-preview">
            <GeneratedTable
                organizationTimeZone="America/Los_Angeles"
                title="Open shifts found"
                columns={[
                    'shift_id',
                    'client',
                    'start_time',
                    'status',
                    'requested_hours',
                    'caregiver',
                ]}
                columnsTruncated={false}
                rows={SAMPLE_GENERATED_TABLE_ROWS}
                rowCount={12}
                pageOffset={0}
                isLoading={false}
                isLoadingMore={false}
                loadError={false}
                hasPrevious={false}
                hasMore={true}
                onPrevious={() => undefined}
                onNext={() => undefined}
                selection={{
                    idColumn: 'shift_id',
                    actions: [
                        {
                            label: 'Start outreach',
                            prompt: 'Create and start an outreach for the selected shifts.',
                            maxRows: 10,
                            maxRowsMessage:
                                'Select 10 or fewer shifts to start outreach.',
                        },
                        {
                            label: 'Find caregivers',
                            prompt: 'Find matching caregivers for the selected shifts.',
                        },
                    ],
                    onAction: () => undefined,
                }}
            />
        </div>
    );
}

// Static waiting card: the earlier phase-cycling animation made the
// section unreadable for review.
function AskUserFlowPreview(): ReactNode {
    return (
        <div className="v3-registry-interaction-preview">
            <AssistantPreviewRow intro="I need your direction before I continue." />
            <ControlledQuestionView
                callId="sample-ask-user-single"
                choiceValues={null}
                presentation={SAMPLE_QUESTION_PRESENTATION}
                requestSubmit={() => Promise.resolve('accepted')}
                resolvedAnswer={null}
                status="waiting"
            />
        </div>
    );
}

function DockedQuestionPreview(): ReactNode {
    return (
        <div className="v3-registry-interaction-preview">
            <QuestionDockContext.Provider
                value={{
                    dismiss: () => undefined,
                    renderComposer: (options) => (
                        <Composer
                            displayName="Phoebe"
                            feature={{
                                kind: 'text',
                                placeholder: 'Message Phoebe…',
                                send: () =>
                                    Promise.reject(
                                        new Error('The preview never sends.'),
                                    ),
                            }}
                            layout="flush"
                            onSend={(content) => options.onSendText(content)}
                            placeholder={
                                options.placeholder ??
                                'Respond to Phoebe’s question or ask something else…'
                            }
                        />
                    ),
                }}
            >
                <ControlledQuestionView
                    callId="sample-docked-question"
                    choiceValues={null}
                    presentation={SAMPLE_QUESTION_PRESENTATION}
                    requestSubmit={() => Promise.resolve('accepted')}
                    resolvedAnswer={null}
                    status="waiting"
                />
            </QuestionDockContext.Provider>
        </div>
    );
}

const APPROVAL_STACK_PREVIEW_ITEMS = [
    {
        ownerId: 'stack-preview-message',
        editor: SAMPLE_APPROVAL_EDITOR,
        presentation: SAMPLE_APPROVAL_PRESENTATION,
    },
    {
        ownerId: 'stack-preview-voice',
        editor: SAMPLE_VOICE_APPROVAL_EDITOR,
        presentation: SAMPLE_VOICE_APPROVAL_PRESENTATION,
    },
    {
        ownerId: 'stack-preview-generic',
        editor: null,
        presentation: SAMPLE_GENERIC_APPROVAL_PRESENTATION,
    },
] as const;

function ApprovalStackPreview(): ReactNode {
    const [iteration, setIteration] = useState(0);
    const [decisions, setDecisions] = useState<
        Readonly<Record<string, 'granted' | 'denied'>>
    >({});
    const [preferredOwnerId, setPreferredOwnerId] = useState<string | null>(
        null,
    );
    const pendingOwnerIds: ReadonlyArray<string> =
        APPROVAL_STACK_PREVIEW_ITEMS.map((item) => item.ownerId).filter(
            (ownerId) => decisions[ownerId] === undefined,
        );
    const stack: ApprovalStack = {
        pendingOwnerIds,
        activeOwnerId:
            preferredOwnerId !== null &&
            pendingOwnerIds.includes(preferredOwnerId)
                ? preferredOwnerId
                : (pendingOwnerIds[0] ?? null),
        activate: setPreferredOwnerId,
    };

    // Once every approval is decided, replay the whole stack like the other
    // animated previews.
    useEffect(() => {
        if (pendingOwnerIds.length > 0) {
            return;
        }
        const handle = window.setTimeout(() => {
            setDecisions({});
            setPreferredOwnerId(null);
            setIteration((current) => current + 1);
        }, 1900);
        return () => window.clearTimeout(handle);
    }, [pendingOwnerIds.length]);

    const decideFor =
        (ownerId: string): SafeRenderContext['requestToolDecision'] =>
        (decision) => {
            window.setTimeout(() => {
                setDecisions((current) => ({
                    ...current,
                    [ownerId]:
                        decision.decision === 'deny' ? 'denied' : 'granted',
                }));
            }, 520);
            return Promise.resolve('accepted');
        };

    return (
        <div className="v3-registry-interaction-preview">
            <ApprovalStackContext.Provider value={stack}>
                <div className="flex flex-col gap-2">
                    {APPROVAL_STACK_PREVIEW_ITEMS.map((item) => (
                        <ApprovalStackSlot
                            key={`${item.ownerId}-${iteration}`}
                            ownerId={item.ownerId}
                            title={collapsedApprovalTitle(
                                item.editor,
                                item.presentation,
                                rawPersonName,
                            )}
                        >
                            <ControlledPermissionView
                                batchCallIds={null}
                                callId={`${item.ownerId}-call-${iteration}`}
                                editor={item.editor}
                                presentation={item.presentation}
                                requestDecision={decideFor(item.ownerId)}
                                scope={NOOP_RENDER_CONTEXT.scope}
                                status={decisions[item.ownerId] ?? 'waiting'}
                            />
                        </ApprovalStackSlot>
                    ))}
                </div>
            </ApprovalStackContext.Provider>
        </div>
    );
}

function AnimatedMessageApprovalPreview(): ReactNode {
    const [iteration, setIteration] = useState(0);
    const [status, setStatus] = useState<'waiting' | 'granted' | 'denied'>(
        'waiting',
    );

    const requestDecision: SafeRenderContext['requestToolDecision'] = async (
        decision,
    ) => {
        window.setTimeout(() => {
            setStatus(decision.decision === 'deny' ? 'denied' : 'granted');
            window.setTimeout(() => {
                setStatus('waiting');
                setIteration((current) => current + 1);
            }, 1900);
        }, 520);
        return 'accepted';
    };

    return (
        <div className="v3-registry-interaction-preview">
            <ControlledPermissionView
                key={iteration}
                batchCallIds={null}
                callId={`sample-message-approval-call-${iteration}`}
                editor={SAMPLE_APPROVAL_EDITOR}
                presentation={SAMPLE_APPROVAL_PRESENTATION}
                requestDecision={requestDecision}
                scope={NOOP_RENDER_CONTEXT.scope}
                status={status}
            />
        </div>
    );
}

function AnimatedVoiceApprovalPreview(): ReactNode {
    const [iteration, setIteration] = useState(0);
    const [status, setStatus] = useState<'waiting' | 'granted' | 'denied'>(
        'waiting',
    );

    const requestDecision: SafeRenderContext['requestToolDecision'] = (
        decision,
    ) => {
        window.setTimeout(() => {
            setStatus(decision.decision === 'deny' ? 'denied' : 'granted');
            window.setTimeout(() => {
                setStatus('waiting');
                setIteration((current) => current + 1);
            }, 1900);
        }, 520);
        return Promise.resolve('accepted');
    };

    return (
        <div className="v3-registry-interaction-preview">
            <ControlledPermissionView
                key={iteration}
                batchCallIds={null}
                callId={`sample-voice-approval-call-${iteration}`}
                editor={SAMPLE_VOICE_APPROVAL_EDITOR}
                presentation={SAMPLE_VOICE_APPROVAL_PRESENTATION}
                requestDecision={requestDecision}
                scope={NOOP_RENDER_CONTEXT.scope}
                status={status}
            />
        </div>
    );
}

function OutreachApprovalPreview(): ReactNode {
    const [phase, setPhase] = useState<'pending' | 'broadcasting' | 'receipt'>(
        'pending',
    );

    function start(): void {
        if (phase !== 'pending') {
            return;
        }
        setPhase('broadcasting');
        window.setTimeout(() => {
            setPhase('receipt');
            window.setTimeout(() => setPhase('pending'), 1900);
        }, 900);
    }

    return (
        <div className="v3-registry-interaction-preview">
            <OutreachCard
                title={
                    phase === 'pending' ? 'Start outreach' : 'Outreach started'
                }
                detail="2 shifts · 2 clients · Sep 1-2"
                stateLabel={phase === 'pending' ? undefined : 'Preparing'}
                broadcasting={phase === 'broadcasting'}
                receipt={phase === 'receipt'}
                action={
                    phase === 'pending'
                        ? {
                              label: 'Start outreach',
                              onAction: start,
                          }
                        : undefined
                }
            />
        </div>
    );
}

function AnimatedOutreachPreview(): ReactNode {
    const [status, setStatus] = useState<OutreachCardStatus>('in_progress');
    const states: ReadonlyArray<readonly [OutreachCardStatus, string]> = [
        ['in_progress', 'Running'],
        ['filled', 'Finished'],
        ['paused', 'Paused'],
        ['pending', 'Preparing'],
        ['cancelled', 'Cancelled'],
    ];
    return (
        <div className="w-full space-y-3">
            <div
                className="flex flex-wrap gap-2"
                aria-label="Preview outreach status"
            >
                {states.map(([value, label]) => (
                    <button
                        key={value}
                        type="button"
                        aria-pressed={status === value}
                        onClick={() => setStatus(value)}
                        className="bg-muted aria-pressed:bg-agent-action-wash aria-pressed:text-agent-action rounded-md px-2 py-1 text-xs"
                    >
                        {label}
                    </button>
                ))}
            </div>
            <OutreachStatusCard
                heading="created"
                status={status}
                viewHref="#outreach"
            />
        </div>
    );
}

function AnimatedGenericApprovalPreview(): ReactNode {
    const [iteration, setIteration] = useState(0);
    const [status, setStatus] = useState<'waiting' | 'granted' | 'denied'>(
        'waiting',
    );

    const requestDecision: SafeRenderContext['requestToolDecision'] = async (
        decision,
    ) => {
        window.setTimeout(() => {
            setStatus(decision.decision === 'deny' ? 'denied' : 'granted');
            window.setTimeout(() => {
                setStatus('waiting');
                setIteration((current) => current + 1);
            }, 1900);
        }, 520);
        return 'accepted';
    };

    return (
        <div className="v3-registry-interaction-preview">
            <ControlledPermissionView
                key={iteration}
                batchCallIds={null}
                callId={`sample-generic-approval-call-${iteration}`}
                editor={null}
                presentation={SAMPLE_GENERIC_APPROVAL_PRESENTATION}
                requestDecision={requestDecision}
                scope={NOOP_RENDER_CONTEXT.scope}
                status={status}
            />
        </div>
    );
}

function CheckInEventsPreview(): ReactNode {
    return (
        <div className="v3-event-lines-preview">
            <EventLine className="pl-0" glyph={<Clock />}>
                Checking back at 2:58 PM · the outreach SMS responses
            </EventLine>
            <EventLine className="pl-0" glyph={<Bell />}>
                Checked back at 2:58 PM · the outreach SMS responses
            </EventLine>
            <EventLine className="pl-0" glyph={<Activity />}>
                Coverage on the Tuesday callout · changed
            </EventLine>
            <EventLine className="pl-0" glyph={<Activity />}>
                Stopped watching · coverage on the Tuesday callout
            </EventLine>
        </div>
    );
}

function RegistryExample({
    item,
    children,
}: Readonly<{
    readonly item: GalleryComponentItem;
    readonly children: ReactNode;
}>): ReactNode {
    return (
        <article
            id={item.id}
            className="v3-registry-card scroll-mt-6"
            data-gallery-id={item.id}
        >
            <header className="v3-registry-card-header">
                <div className="min-w-0">
                    <h3>{item.label}</h3>
                    <code>{item.component}</code>
                </div>
                <div className="v3-registry-events">
                    {item.eventTypes.map((eventType) => (
                        <AdminBadge key={eventType} tone="neutral">
                            {eventType}
                        </AdminBadge>
                    ))}
                </div>
            </header>
            <div className="v3-registry-preview">{children}</div>
        </article>
    );
}
